import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import { useSnackbar } from 'ui-components';
import { Error403 } from '~/components/Error403';
import { FEATURE_FLAGS } from '~/constants';
import { useCreateMediaKit } from '~/hooks/api/media-kits/create';
import { useListMediaKits } from '~/hooks/api/media-kits/list';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { selectCreator } from '~/selectors/creator';

import { CreatorProfileSettingsModal } from '../CreatorProfileSettings/CreatorProfileSettingsModal';
import { useProfileSettingsModalStore } from '../CreatorProfileSettings/useProfileSettingsModal';
import { MediaKitsGrid } from '../CustomShareablesListing/MediaKitsGrid';
import { MediaKitsLoadingGrid } from '../CustomShareablesListing/MediaKitsLoadingGrid';
import { CreateMediaKitButton } from '../CustomShareablesListing/components/CreateMediaKitButton';
import { getMediaKitPayload } from '../CustomShareablesListing/utils';
import { MediaKitsLanding } from './MediaKitsLanding';

export const CreatorMediaKits = () => {
  const { isEnabled: isMediaKitsForCreatorsEnabled } = useFeature(
    FEATURE_FLAGS.foamCreatorMediaKits,
  );

  const router = useRouter();
  const snackbar = useSnackbar();

  const creator = useSelector(selectCreator);
  const createMediaKit = useCreateMediaKit();
  const { openModal } = useProfileSettingsModalStore();

  const mediaKits = useListMediaKits();

  const [isScrolled, setIsScrolled] = useState(false);

  // Extract media kits list for empty state check
  const mediaKitsList = useMemo(() => {
    return mediaKits.data?.pages.map((page) => page.results).flat() || [];
  }, [mediaKits.data]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 100;
      setIsScrolled(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const createMediaKitFlow = async () => {
    if (!creator) return;

    try {
      const resp = await createMediaKit.mutateAsync(
        getMediaKitPayload(creator),
      );

      if (resp.data?.id) {
        router.push(`/media-kits/${resp.data.id}/builder`);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'There has been an error. Please try again';
      snackbar.error({
        description: errorMessage,
      });
    }
  };

  const onAddClicked = async () => {
    if (creator) {
      // Check if email contact is missing, open modal if so
      if (!creator.hasEmailContact) {
        openModal(createMediaKitFlow);
        return;
      }

      await createMediaKitFlow();
    }
  };

  if (!isMediaKitsForCreatorsEnabled) return <Error403 />;

  return (
    <div>
      {/* Sticky header that appears on scroll */}
      <div
        className={`fixed inset-x-0 top-0 z-50 bg-neutral-0 shadow-md transition-transform duration-300 ${
          isScrolled ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="mx-auto flex max-w-full items-center justify-between px-6 py-4">
          <h2 className="text-root-size-body-xl font-500">
            Your first Media Kit in minutes
          </h2>
          <CreateMediaKitButton
            onClick={onAddClicked}
            isDisabled={createMediaKit.isLoading}
            trackId="create-media-kit-btn-unmanaged-creators"
            hideIcon
          />
        </div>
      </div>

      {match(mediaKits.status)
        .with('loading', () => <MediaKitsLoadingGrid />)
        .with('success', () =>
          mediaKitsList.length > 0 ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-24 font-500">Media Kits</h1>
                <CreateMediaKitButton
                  onClick={onAddClicked}
                  isDisabled={createMediaKit.isLoading}
                  trackId="create-media-kit-btn-unmanaged-creators"
                />
              </div>
              <MediaKitsGrid mediaKits={mediaKits} />
            </div>
          ) : (
            <MediaKitsLanding
              onCreateClick={onAddClicked}
              isCreating={createMediaKit.isLoading}
            />
          ),
        )
        .with('error', () => (
          <MediaKitsLanding
            onCreateClick={onAddClicked}
            isCreating={createMediaKit.isLoading}
          />
        ))
        .exhaustive()}

      <CreatorProfileSettingsModal />
    </div>
  );
};
